import type { CSSProperties } from 'react';

import { cn } from 'utils/cn';
import { CloseIcon } from '../Icons';
import { Subtitle } from '../LegacyText';
import Text from '../Text';
import type { CalloutOverrides, CalloutProps } from './callout.types';
import { CalloutType } from './callout.types';
import { CalloutIcon } from './components/CalloutIcon';

export type { CalloutOverrides, CalloutProps };
export { CalloutType };

const TYPE_COLOR: Partial<Record<CalloutType, string>> = {
  [CalloutType.INFO]:
    'text-info bg-[var(--tw-blue-05)] dark:text-[var(--tw-blue-10)] dark:bg-[var(--tw-blue-90)]',
  [CalloutType.ERROR]:
    'text-danger bg-[var(--tw-red-05)] dark:text-[var(--tw-red-10)] dark:bg-[var(--tw-red-90)]',
  [CalloutType.WARNING]:
    'text-warning bg-[var(--tw-yellow-05)] dark:text-[var(--tw-yellow-10)] dark:bg-[var(--tw-yellow-100)]',
  [CalloutType.SUCCESS]:
    'text-success bg-[var(--tw-green-05)] dark:text-[var(--tw-green-10)] dark:bg-[var(--tw-green-90)]',
  // Pre-migration NEUTRAL explicitly used baseColors.grey['05'] (#fafafa —
  // the original grey ramp) regardless of active theme. Post-migration
  // `--tw-grey-05` is aliased to newGrey['05'] (#f5f5f5 — ~5 units darker),
  // so keep the hex literal here to preserve the subtle callout background.
  [CalloutType.NEUTRAL]:
    'text-primary bg-[#fafafa] dark:text-[var(--tw-grey-10)] dark:bg-[var(--tw-grey-90)]',
};

const TYPE_BORDER: Partial<Record<CalloutType, string>> = {
  [CalloutType.INFO]:
    'border border-[var(--tw-blue-20)] dark:border-[var(--tw-blue-50)]',
  [CalloutType.ERROR]:
    'border border-[var(--tw-red-20)] dark:border-[var(--tw-red-50)]',
  [CalloutType.WARNING]:
    'border border-[var(--tw-yellow-20)] dark:border-[var(--tw-yellow-50)]',
  [CalloutType.SUCCESS]:
    'border border-[var(--tw-green-20)] dark:border-[var(--tw-green-50)]',
};

const Callout = ({
  headline,
  children,
  type,
  overrides,
  customIcon,
  action,
  hideIcon,
  className,
  rounded,
  withBorder = true,
  hasCloseButton,
  iconOnTop,
  dark: _dark,
  onCloseClick,
}: CalloutProps) => {
  const colorClass = type
    ? (TYPE_COLOR[type] ?? 'text-secondary bg-background-page')
    : 'text-secondary bg-background-page';

  const borderEntry = type ? TYPE_BORDER[type] : undefined;
  const borderClass =
    withBorder && !overrides?.borderStyle && borderEntry ? borderEntry : '';

  const inlineStyle: CSSProperties = {};
  if (withBorder && overrides?.borderStyle)
    inlineStyle.border = overrides.borderStyle;
  if (overrides?.background) inlineStyle.background = overrides.background;
  if (overrides?.color) inlineStyle.color = overrides.color;
  if (overrides?.padding) inlineStyle.padding = overrides.padding;

  return (
    <div
      className={cn(
        'flex gap-xs3 w-full py-xs3 px-sm1',
        !iconOnTop && 'items-center',
        rounded ? 'rounded-xSmall' : 'rounded-medium',
        borderClass,
        colorClass,
        className,
      )}
      style={Object.keys(inlineStyle).length > 0 ? inlineStyle : undefined}
    >
      {!hideIcon &&
        (customIcon ?? <CalloutIcon overrides={overrides} type={type} />)}
      <div className="flex grow items-center justify-between gap-xs2">
        <div className="flex gap-xs2 items-center w-full">
          {headline && <Subtitle as="p">{headline}</Subtitle>}
          <Text as="div" className="w-full">
            {children}
          </Text>
        </div>
        {action && (
          <div className="inline-flex items-center justify-end gap-xs1">
            {action}
          </div>
        )}
        {hasCloseButton && (
          <button
            aria-label="Close"
            className="inline-flex cursor-pointer border-0 bg-transparent p-0"
            type="button"
            onClick={onCloseClick}
          >
            <CloseIcon />
          </button>
        )}
      </div>
    </div>
  );
};

export { CalloutIcon };

export default Callout;
